'use client';

import Image from 'next/image';
import { useState, useEffect, useRef, useCallback } from 'react';
import { PiArrowArcRightBold } from 'react-icons/pi';

const avatars = [
  {
    src: '/avatars/avatar-jim.jpg',
    name: 'Jim Tornqvist',
    position: 'Director',
    company: '3Doodler',
  },
  {
    src: '/avatars/avatar-diginex.jpg',
    name: 'Paul Ewing',
    position: 'CFO',
    company: 'Diginex',
  },
  {
    src: '/avatars/avatar-mtc.jpg',
    name: 'Andrew Ford',
    position: 'Founder',
    company: 'MTC Coffee',
  },
  {
    src: '/avatars/avatar-ampd.jpg',
    name: 'Brandon Ng',
    position: 'Founder',
    company: 'AMPD Energy',
  },
  {
    src: '/avatars/avatar-pms.jpg',
    name: 'Mark Ho',
    position: 'Director',
    company: 'Pacific Medical',
  },
  {
    src: '/avatars/avatar-floship.jpg',
    name: 'Eric Pong',
    position: 'Founder',
    company: 'Floship',
  },
  {
    src: '/avatars/avatar-kidkraft.jpg',
    name: 'Michael Sagan',
    position: 'VP Ops',
    company: 'KidKraft',
  },
];

interface SocialProofProps {
  className?: string;
  theme?: 'light' | 'dark';
  position?: 'left' | 'center';
  arrow?: 'left' | 'right';
}

export default function SocialProof({
  className = '',
  theme = 'dark',
  position = 'left',
  arrow = 'right',
}: SocialProofProps) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [animatingStars, setAnimatingStars] = useState<boolean>(false);
  const [activeStars, setActiveStars] = useState<number[]>([]);
  const [userHasInteracted, setUserHasInteracted] = useState<boolean>(false);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startInitialAutoplay = useCallback(() => {
    let currentIndex = 0;
    const cycle = () => {
      setActiveIdx(currentIndex);
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);

      autoplayTimerRef.current = setTimeout(() => {
        if (userHasInteracted) {
          setActiveIdx(null);
          return;
        }
        const nextIndex = (currentIndex + 1) % avatars.length;

        if (nextIndex === 0) {
          setActiveIdx(null);
          autoplayTimerRef.current = setTimeout(() => {
            if (!userHasInteracted) {
              currentIndex = 0;
              cycle();
            }
          }, 5000);
        } else {
          currentIndex = nextIndex;
          cycle();
        }
      }, 1500);
    };
    cycle();
  }, [userHasInteracted]);

  useEffect(() => {
    if (!userHasInteracted) {
      startInitialAutoplay();
    }
    return () => {
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
    };
  }, [startInitialAutoplay, userHasInteracted]);

  useEffect(() => {
    if (activeIdx !== null) {
      setAnimatingStars(true);
      setActiveStars([]);
      const animationDelay = 150;
      for (let i = 0; i < 5; i++) {
        setTimeout(() => {
          setActiveStars((prev) => [...prev, i]);
        }, animationDelay * i);
      }
    } else {
      setAnimatingStars(false);
      setActiveStars([]);
    }
  }, [activeIdx]);

  return (
    <div
      className={`relative ${className} ${
        position === 'center'
          ? 'grid grid-cols-1 items-center sm:grid-cols-2'
          : 'flex flex-col sm:flex-row sm:items-center'
      }`}
    >
      {/* Avatars */}
      <div
        className={` ${
          position === 'center' ? 'flex justify-center pr-0 sm:justify-end sm:pr-2' : 'flex'
        } ${position === 'center' ? 'h-14' : 'h-[58px] sm:h-[65px]'} `}
      >
        <div className="relative flex">
          {avatars.map((avatar, idx) => (
            <div
              key={idx}
              className={`relative -ml-4 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-white drop-shadow-md transition-all duration-300 first:ml-0 sm:h-14 sm:w-14 ${
                activeIdx === idx
                  ? 'z-10 scale-110'
                  : activeIdx === null
                    ? ''
                    : theme === 'light'
                      ? 'avatar-muted-light grayscale'
                      : 'avatar-muted-dark grayscale'
              }`}
              style={{ transform: activeIdx === idx ? 'scale(1.2)' : 'scale(1)', left: 0 }}
              onMouseEnter={() => {
                setActiveIdx(idx);
                setUserHasInteracted(true);
                if (autoplayTimerRef.current) {
                  clearTimeout(autoplayTimerRef.current);
                  autoplayTimerRef.current = null;
                }
              }}
              onMouseLeave={() => {
                setActiveIdx(null);
              }}
            >
              <Image
                src={avatar.src}
                alt={avatar.name}
                width={56}
                height={56}
                className="rounded-full object-cover"
                style={{ filter: 'contrast(0.7)' }}
              />
            </div>
          ))}
          <PiArrowArcRightBold
            className={`absolute ${
              arrow === 'right'
                ? '-right-20 rotate-[60deg]'
                : 'bottom-12 left-6 rotate-[60deg] -scale-x-100'
            } h-14 w-14 text-white sm:hidden ${theme === 'light' ? 'hidden' : ''}`}
          />
        </div>
      </div>

      {/* Rating + Text */}
      <div
        className={`${
          position === 'center'
            ? 'mt-4 flex items-center justify-center pl-0 sm:mt-0 sm:items-start sm:justify-start sm:pl-2'
            : 'mt-4 sm:ml-4 sm:mt-0'
        } flex flex-col text-left ${
          theme === 'light' ? 'text-black' : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]'
        }`}
      >
        {/* Stars */}
        <div className="mb-1 flex">
          {Array.from({ length: 5 }).map((_, idx) => (
            <svg
              key={idx}
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 fill-current drop-shadow-sm transition-colors duration-200 ${
                animatingStars
                  ? activeStars.includes(idx)
                    ? 'text-yellow-300'
                    : theme === 'light'
                      ? 'text-gray-300'
                      : 'text-white'
                  : 'text-yellow-300'
              }`}
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.946a1 1 0 00.95.69h4.148c.969 0 1.371 1.24.588 1.81l-3.36 2.444a1 1 0 00-.364 1.118l1.286 3.946c.3.921-.755 1.688-1.54 1.118l-3.36-2.444a1 1 0 00-1.176 0l-3.36 2.444c-.784.57-1.838-.197-1.54-1.118l1.287-3.946a1 1 0 00-.364-1.118L2.08 9.373c-.783-.57-.38-1.81.588-1.81h4.148a1 1 0 00.95-.69l1.283-3.946z" />
            </svg>
          ))}
        </div>

        {/* Text */}
        <div
          className={`min-w-[200px] text-sm ${position === 'center' ? 'text-center sm:text-left' : ''}`}
        >
          {activeIdx === null ? (
            <span className="font-semibold">20+ Years of NetSuite Delivery</span>
          ) : (
            <span>
              <span className="font-semibold">{avatars[activeIdx].name}</span>,{' '}
              {avatars[activeIdx].position} at {avatars[activeIdx].company}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
